import React, { useMemo } from 'react'
import { Col, Row } from 'antd'
import { filter, includes, isEmpty, map, values } from 'lodash'
import PropTypes from 'prop-types'

import {
  FormLazyTreeSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  antFormItemNamePropType,
  idPropType,
  treeOptionPropType,
} from '../../../common/prop-types'

import { INGREDIENT_PHRASE_FIELD_NAMES } from '../../constants'
import { tokenizeTextByNewLine } from '../../utils'
import { validatePhrase } from '../forms/validators'

const IngredientPairingFormSection = ({
  ingredientId,
  disabled,
  form,
  textareaName,
  textareaLabel,
  textareaPlaceholder,
  ingredientListLabel,
  ingredientListName,
  ingredientListPlaceholder,
  ingredientOptions,
  groupedFlatTreeDataByValue,
  flatTreeData,
  initialValue,
}) => {
  const textareaRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          const currentPhrases = tokenizeTextByNewLine(value)

          const otherPhraseFieldName = filter(
            values(INGREDIENT_PHRASE_FIELD_NAMES),
            textareaName,
          )

          const otherPhrases = map(otherPhraseFieldName, fieldName =>
            tokenizeTextByNewLine(getFieldValue(fieldName)),
          )

          try {
            validatePhrase(currentPhrases, otherPhrases)

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [textareaName],
  )

  const ingredientIdsRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (includes(value, ingredientId)) {
            throw new Error(`Can't select owning ingredient`)
          }

          const phrasesText = getFieldValue(textareaName)

          if (!isEmpty(value) && isEmpty(phrasesText)) {
            return Promise.reject(
              new Error(
                `At least a valid placeholder phrase needed in ${textareaName}`,
              ),
            )
          }
          return Promise.resolve()
        },
      }),
    ],
    [ingredientId, textareaName],
  )

  return (
    <Row gutter={[16]}>
      <Col span={12}>
        <FormTextareaInput
          disabled={disabled}
          name={textareaName}
          label={textareaLabel}
          labelAbove
          autoSize
          placeholder={textareaPlaceholder}
          rules={textareaRules}
        />
      </Col>

      <Col span={12}>
        <FormLazyTreeSelect
          disabled={disabled}
          form={form}
          name={ingredientListName}
          label={ingredientListLabel}
          showSearch
          labelAbove
          allowClear
          treeCheckable
          treeCheckStrictly
          treeData={ingredientOptions}
          rules={ingredientIdsRules}
          placeholder={ingredientListPlaceholder}
          groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
          flatTreeData={flatTreeData}
          initialValue={initialValue}
          areMissingChildrenTagsHighlighted
        />
      </Col>
    </Row>
  )
}

IngredientPairingFormSection.propTypes = {
  ingredientId: idPropType.isRequired,
  form: PropTypes.object.isRequired,
  textareaName: antFormItemNamePropType.isRequired,
  textareaLabel: PropTypes.string,
  textareaPlaceholder: PropTypes.string,
  ingredientListName: antFormItemNamePropType.isRequired,
  ingredientListLabel: PropTypes.string,
  ingredientListPlaceholder: PropTypes.string,
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType).isRequired,
  groupedFlatTreeDataByValue: PropTypes.object.isRequired,
  flatTreeData: PropTypes.arrayOf(PropTypes.object),
  initialValue: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      title: PropTypes.string,
    }),
  ),
  disabled: PropTypes.bool,
}

IngredientPairingFormSection.defaultProps = {
  textareaLabel: 'Phrases (new line separated)',
  textareaPlaceholder: 'Please input phrases...',
  ingredientListLabel: 'Ingredients',
  ingredientListPlaceholder: 'Please select ingredient...',
  flatTreeData: [],
  initialValue: [],
  disabled: false,
}

export default IngredientPairingFormSection
